import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import './ConfirmDialog.css';

const ConfirmContext = createContext(null);

function detectLabels(message) {
  const m = message.toLowerCase();
  if (m.includes('lösch')) return { confirmLabel: 'Löschen', variant: 'danger', kind: 'delete' };
  if (m.includes('abmeld')) return { confirmLabel: 'Abmelden', variant: 'logout', kind: 'logout' };
  return { confirmLabel: 'OK', variant: 'primary', kind: 'info' };
}

function HoldButton({ onConfirm, label, variant, holdMs = 3000 }) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const doneRef = useRef(false);

  const start = (e) => {
    e.preventDefault();
    if (doneRef.current) return;
    startRef.current = performance.now();
    const tick = (now) => {
      const pct = Math.min((now - startRef.current) / holdMs * 100, 100);
      setProgress(pct);
      if (pct >= 100) { doneRef.current = true; onConfirm(); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const cancel = () => {
    cancelAnimationFrame(rafRef.current);
    if (!doneRef.current) setProgress(0);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const secs = Math.ceil((holdMs - progress / 100 * holdMs) / 1000);

  return (
    <button
      className={`confirm-btn confirm-btn--${variant} confirm-btn--hold`}
      onMouseDown={start}
      onMouseUp={cancel}
      onMouseLeave={cancel}
      onTouchStart={start}
      onTouchEnd={cancel}
      onTouchCancel={cancel}
    >
      <span className="confirm-btn-hold-fill" style={{ width: `${progress}%` }} />
      <span className="confirm-btn-hold-label">
        {progress > 0 ? `${secs}s…` : label}
      </span>
    </button>
  );
}

function DialogIcon({ kind }) {
  if (kind === 'logout') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    );
  }
  if (kind === 'delete') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function ConfirmDialog({ message, onConfirm, onCancel, holdMs }) {
  const { confirmLabel, variant, kind } = detectLabels(message);

  return (
    <div className="confirm-overlay" onClick={onCancel}>
      <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
        <div className={`confirm-icon-wrap confirm-icon-wrap--${variant}`}>
          <DialogIcon kind={kind} />
        </div>
        <p className="confirm-message">{message}</p>
        <div className="confirm-actions">
          <button className="confirm-btn confirm-btn--cancel" onClick={onCancel}>
            Abbrechen
          </button>
          {holdMs ? (
            <HoldButton onConfirm={onConfirm} label={confirmLabel} variant={variant} holdMs={holdMs} />
          ) : (
            <button className={`confirm-btn confirm-btn--${variant}`} onClick={onConfirm} autoFocus>
              {confirmLabel}
            </button>
          )}
        </div>
        {holdMs && (
          <p className="confirm-hold-hint">3 Sekunden gedrückt halten zum Bestätigen</p>
        )}
      </div>
    </div>
  );
}

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((message, opts = {}) => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setDialog({ message, holdMs: opts.holdMs });
    });
  }, []);

  const close = (result) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setDialog(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <ConfirmDialog
          message={dialog.message}
          holdMs={dialog.holdMs}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
